/**
 * /tools/api - calls the Task API and the Web API for the same operation, side by side, and diffs
 * what they return.
 *
 * Gated behind the `fe-api-tool` LaunchDarkly flag. The flag is read here rather than around the
 * <Route> in Router.component.jsx because the router is built once, above AppInitialization, while
 * `launchDarkly.userClient` only exists after /startup has answered - so a check up there would
 * always see null. With the flag off this renders the app's own NotFound, which is what
 * /tools/api did before the route existed.
 */
import React, {useEffect, useRef, useState} from "react";
import PropTypes from "prop-types";
import {launchDarkly} from "utils/launchDarkly";
import {NotFound} from "components/errorpage/ErrorPage.component.jsx";
import styles from "./apiTool.module.css";
import {API_LABELS, defaultParams, endpoints, getEndpoint, isPaired, missingParams, sideKeys} from "./endpoints.js";
import {apiRoot, callSide, describeSession, fetchCompanies, fetchStartup, fetchTasks} from "./client.js";
import {compareResults} from "./compare.js";
import Comparison from "./Comparison.component.jsx";

/*
 * The operation and side shapes are documented at the top of endpoints.js, and the request/response
 * record is what client.js#callSide returns. These describe those, and are shared by the components
 * below rather than restated per component.
 */
const SIDE_KEY = PropTypes.oneOf(Object.keys(API_LABELS));

const PARAM_SHAPE = PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string,
    help: PropTypes.string,
    placeholder: PropTypes.string,
    in: PropTypes.oneOf(["path", "query"]),
    appliesTo: PropTypes.arrayOf(SIDE_KEY).isRequired,
    options: PropTypes.string,
    choices: PropTypes.arrayOf(PropTypes.string),
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

/** A dropdown's contents. `status` is what decides between a select and a fallback text box. */
const SOURCE_SHAPE = PropTypes.shape({
    status: PropTypes.oneOf(["loading", "ready", "error"]).isRequired,
    blankLabel: PropTypes.string.isRequired,
    emptyLabel: PropTypes.string.isRequired,
    items: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        title: PropTypes.string,
    })).isRequired,
});

const RECORD_SHAPE = PropTypes.shape({
    api: SIDE_KEY,
    request: PropTypes.shape({
        method: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
        headers: PropTypes.object.isRequired,
        body: PropTypes.any,
        cookieNames: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
    // absent when the call never went out - `error` carries the reason instead
    response: PropTypes.shape({
        status: PropTypes.number,
        statusText: PropTypes.string,
        headers: PropTypes.object,
        bytes: PropTypes.number,
        finalUrl: PropTypes.string,
    }),
    error: PropTypes.string,
    durationMs: PropTypes.number,
    items: PropTypes.array,
    rawBody: PropTypes.string,
});

function SessionBadge({session}) {
    if (session == null) {
        return <span className={`${styles.badge} ${styles.badgeUnknown}`}>checking session…</span>;
    }
    if (session.error) {
        return (
            <>
                <span className={`${styles.badge} ${styles.badgeOff}`}>no session</span>
                <span className={styles.muted}>{session.error}</span>
            </>
        );
    }
    return (
        <>
            <span className={`${styles.badge} ${styles.badgeOk}`}>signed in</span>
            <span>
                {session.name || session.email}
                <span className={styles.muted}>
                    {" "}{session.email} · ctx {session.contextId ?? "?"} · {session.companyName}
                </span>
            </span>
        </>
    );
}

SessionBadge.propTypes = {
    // null until /startup answers; `error` set instead of the rest when it fails
    session: PropTypes.shape({
        error: PropTypes.string,
        name: PropTypes.string,
        email: PropTypes.string,
        contextId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        companyName: PropTypes.string,
    }),
};

/** The blank first option doubles as where a list that is still loading or empty says so. */
function blankOptionLabel(source) {
    if (source.status === "loading") {
        return "loading…";
    }
    if (source.items.length === 0) {
        return source.emptyLabel;
    }
    return source.blankLabel;
}

/**
 * A parameter is a plain text box unless it declares an `options` source, in which case it is a
 * dropdown of labelled values and what gets sent is the value - the company's id, the task's uid.
 *
 * An empty list and a list that failed to load are deliberately different: empty says so ("No
 * tasks") and stays a disabled dropdown, while a failure falls back to the text box so an
 * arbitrary value is still reachable. This is a debugging tool, and an id outside your own list is
 * fair game - but "this company has nothing" is an answer, not a reason to start typing.
 */
function ParamField({param, value, onChange, sources}) {
    const source = param.options ? sources[param.options] : null;

    const label = (
        <>
            {param.label}{" "}
            <span className={styles.muted}>
                ({param.appliesTo.map(key => API_LABELS[key].split(" ")[0]).join(" + ")})
            </span>
        </>
    );

    // a fixed enum from the spec: no blank option, since these params are the ones that are required
    if (param.choices != null) {
        return (
            <label title={param.help}>
                {label}
                <select
                    className={styles.optionSelect}
                    value={value}
                    onChange={event => onChange(event.target.value)}
                >
                    {param.choices.map(choice => (
                        <option key={choice} value={choice}>{choice}</option>
                    ))}
                </select>
            </label>
        );
    }

    if (source != null && source.status !== "error") {
        const empty = source.items.length === 0;
        return (
            <label title={param.help}>
                {label}
                <select
                    className={styles.optionSelect}
                    value={value}
                    disabled={empty}
                    onChange={event => onChange(event.target.value)}
                >
                    <option value="">{blankOptionLabel(source)}</option>
                    {source.items.map(item => (
                        <option key={item.value} value={item.value} title={item.title}>
                            {item.label}
                        </option>
                    ))}
                </select>
            </label>
        );
    }

    return (
        <label title={param.help}>
            {label}
            <input
                type="text"
                placeholder={param.placeholder}
                value={value}
                onChange={event => onChange(event.target.value)}
            />
        </label>
    );
}

ParamField.propTypes = {
    param: PARAM_SHAPE.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    // keyed by the `options` name a param declares, e.g. "companies" or "tasks"
    sources: PropTypes.objectOf(SOURCE_SHAPE).isRequired,
};

/**
 * Read-only paired operations get one "Run both" button. Anything else gets a button per side:
 *
 * - A **mutating** operation must never be run as a pair. Approving a task through the Task API and
 *   then again through the Web API performs the action twice - the second call usually fails
 *   because the task is already resolved, so the "comparison" would be noise bought with real
 *   damage. It also has to be armed first, and disarms itself after a single run.
 * - A **single-sided** operation has nothing to pair with; ~14 Task API and ~200 Web API operations
 *   exist on one API only.
 */
export function RunControls({endpoint, running, armed, onArm, onRun}) {
    const sides = sideKeys(endpoint);
    const canPair = isPaired(endpoint) && !endpoint.mutating;

    if (canPair) {
        return (
            <button className={styles.primary} onClick={() => onRun(sides)} disabled={running}>
                {running ? "Running…" : "Run both"}
            </button>
        );
    }

    return (
        <div className={styles.runGroup}>
            {endpoint.mutating &&
                <label className={`${styles.check} ${styles.arm}`}>
                    <input type="checkbox" checked={armed} onChange={event => onArm(event.target.checked)}/>
                    <strong>changes data</strong> — arm to run
                </label>
            }
            {!endpoint.mutating && sides.length === 1 &&
                <span className={styles.muted}>
                    only on the {API_LABELS[sides[0]]} — nothing to compare against
                </span>
            }
            {sides.map(key => (
                <button
                    key={key}
                    className={styles.primary}
                    disabled={running || (endpoint.mutating && !armed)}
                    onClick={() => onRun([key])}
                >
                    {running ? "Running…" : `Run ${API_LABELS[key].replace(" (Visma)", "")}`}
                </button>
            ))}
        </div>
    );
}

RunControls.propTypes = {
    /*
     * Only the two fields this component reads. The whole operation shape is documented at the top
     * of endpoints.js, but demanding it here would reject a caller that legitimately passes less.
     */
    endpoint: PropTypes.shape({
        // read through sideKeys() and isPaired(); either side may be absent
        sides: PropTypes.object.isRequired,
        mutating: PropTypes.bool,
    }).isRequired,
    running: PropTypes.bool.isRequired,
    // only meaningful for a mutating operation, which disarms itself after a single run
    armed: PropTypes.bool.isRequired,
    onArm: PropTypes.func.isRequired,
    onRun: PropTypes.func.isRequired,
};

function SidePanel({apiKey, record}) {
    const response = record.response;
    const failed = Boolean(record.error) || response?.status !== 200;
    const query = new URL(record.request.url).search;

    return (
        <div className={`${styles.side} ${apiKey === "task" ? styles.sideTask : styles.sideWeb}`}>
            <h2>{API_LABELS[apiKey]} <span>{record.request.method}</span></h2>
            <dl className={styles.meta}>
                <dt>URL</dt>
                <dd>{record.request.url}</dd>
                <dt>Query</dt>
                <dd>{query || <span className={styles.muted}>none</span>}</dd>
                {response?.finalUrl && <><dt>Redirected to</dt><dd>{response.finalUrl}</dd></>}
                <dt>Status</dt>
                <dd className={`${styles.status} ${failed ? styles.statusDifferent : styles.statusEqual}`}>
                    {record.error ?? `${response.status} ${response.statusText}`}
                </dd>
                <dt>Time</dt>
                <dd>{record.durationMs} ms</dd>
                <dt>Size</dt>
                <dd>{response ? `${response.bytes} B` : "–"}</dd>
                <dt>Items</dt>
                <dd>{record.items ? record.items.length : "–"}</dd>
            </dl>

            <details>
                <summary>Request headers</summary>
                <dl className={styles.meta}>
                    {Object.entries(record.request.headers).map(([name, value]) => (
                        <React.Fragment key={name}><dt>{name}</dt><dd>{value}</dd></React.Fragment>
                    ))}
                    <dt>cookie</dt>
                    <dd>
                        sent by the browser (<code>credentials: include</code>)<br/>
                        <span className={styles.muted}>
                            script-visible: {record.request.cookieNames?.join(", ") || "none"};
                            the HttpOnly session cookie is not listed but is still sent
                        </span>
                    </dd>
                </dl>
            </details>

            {response &&
                <details>
                    <summary>Response headers <span className={styles.muted}>(CORS-exposed only)</span></summary>
                    <dl className={styles.meta}>
                        {Object.entries(response.headers).map(([name, value]) => (
                            <React.Fragment key={name}><dt>{name}</dt><dd>{value}</dd></React.Fragment>
                        ))}
                    </dl>
                </details>
            }

            {record.rawBody &&
                <details>
                    <summary>Non-JSON body</summary>
                    <pre>{record.rawBody}</pre>
                </details>
            }
        </div>
    );
}

SidePanel.propTypes = {
    apiKey: SIDE_KEY.isRequired,
    record: RECORD_SHAPE.isRequired,
};

export function ApiTool() {
    const [session, setSession] = useState(null);
    // both dropdown sources carry a status of loading, ready or error alongside their items
    const [companies, setCompanies] = useState({status: "loading", items: []});
    const [tasks, setTasks] = useState({status: "loading", items: []});
    const [endpointId, setEndpointId] = useState(endpoints[0].id);
    const [params, setParams] = useState(() => defaultParams(endpoints[0]));
    const [options, setOptions] = useState({
        ignoreCookieAuth: true,
        sendSessionHash: true,
        sendContextId: false,
    });
    const [run, setRun] = useState(null);
    const [running, setRunning] = useState(false);
    const [error, setError] = useState(null);
    // mutating operations stay disabled until deliberately armed, and disarm again after one run
    const [armed, setArmed] = useState(false);
    const [height, setHeight] = useState(null);
    const rootRef = useRef(null);

    const endpoint = getEndpoint(endpointId);

    /*
     * `body { overflow: hidden !important }` in global.scss means the document never scrolls, so
     * this page has to be its own scroll region. Its height is whatever the navigation above it
     * leaves, which is not a fixed number - it depends on fe-vsn-navigation - so measure rather
     * than hard-code it. Setting the height does not move the element, so this cannot loop.
     */
    useEffect(() => {
        const measure = () => {
            const top = rootRef.current?.getBoundingClientRect().top ?? 0;
            setHeight(`calc(100vh - ${Math.round(top)}px)`);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    async function loadSession() {
        setSession(null);
        try {
            const result = await fetchStartup();
            setSession(result.ok
                ? describeSession(result.body)
                : {error: `GET /startup → ${result.status} ${result.statusText}`});
        } catch (caught) {
            setSession({error: `${caught.name}: ${caught.message}`});
        }
    }

    /** Populates the companyId dropdown. Only a failed call falls back to a text box. */
    async function loadCompanies() {
        try {
            const result = await fetchCompanies();
            setCompanies(result.ok
                ? {status: "ready", items: result.companies}
                : {status: "error", items: []});
        } catch {
            setCompanies({status: "error", items: []});
        }
    }

    useEffect(() => {
        loadSession();
        loadCompanies();
    }, []);

    const companyId = params.companyId ?? "";
    const wantsTaskOptions = endpoint.params.some(param => param.options === "tasks");

    /*
     * The task list belongs to whichever company is selected, so it reloads when that changes -
     * and any uid already picked is dropped, because it need not exist in the new company.
     */
    useEffect(() => {
        if (!wantsTaskOptions) {
            return undefined;
        }
        let cancelled = false;
        setTasks({status: "loading", items: []});
        setParams(previous => (previous.taskUids ? {...previous, taskUids: ""} : previous));
        fetchTasks(companyId).then(
            result => !cancelled && setTasks(result.ok
                ? {status: "ready", items: result.tasks}
                : {status: "error", items: []}),
            () => !cancelled && setTasks({status: "error", items: []}),
        );
        return () => {
            cancelled = true;
        };
    }, [companyId, wantsTaskOptions]);

    /**
     * @param {string[]} which the sides to call. Both at once for a read-only paired operation;
     *   a single side for a mutating one, because issuing the same write to both APIs would
     *   perform the action twice.
     */
    async function runSides(which) {
        const missing = missingParams(endpoint, params);
        if (missing.length > 0) {
            setError(`Fill in ${missing.join(", ")} first - ${missing.length === 1 ? "it is" : "they are"} part of the path.`);
            return;
        }

        setError(null);
        setRunning(true);
        try {
            const records = await Promise.all(
                which.map(key => callSide(key, endpoint.sides[key], params, options)),
            );
            const byApi = Object.fromEntries(which.map((key, index) => [key, records[index]]));

            const ranBoth = which.length === 2;
            const bothOk = ranBoth && records.every(record => record.response?.status === 200);
            setRun({
                endpointId: endpoint.id,
                ranAt: new Date().toISOString(),
                which,
                ...byApi,
                comparison: bothOk
                    ? compareResults(byApi.task.items ?? [], byApi.web.items ?? [], endpoint.compare)
                    : null,
            });

            if (ranBoth && !bothOk) {
                setError("One of the calls did not return 200, so there is nothing to compare. See the panels below, and the two entries in the Network tab.");
            }
            // a mutating call is armed for one run only
            setArmed(false);
        } catch (caught) {
            setError(`${caught.name}: ${caught.message}`);
        } finally {
            setRunning(false);
        }
    }

    /*
     * Both dropdowns show a human label and send an opaque value, so they are normalised to the
     * same shape here. The value is what lands on the query string.
     */
    const sources = {
        companies: {
            status: companies.status,
            blankLabel: "All companies",
            emptyLabel: "No companies",
            items: companies.items.map(company => ({
                value: String(company.id),
                label: `${company.name}${company.id === session?.contextId ? " (current)" : ""} — ${company.id}`,
            })),
        },
        tasks: {
            status: tasks.status,
            blankLabel: "All tasks",
            emptyLabel: "No tasks",
            items: tasks.items.map(task => ({
                value: task.uid,
                label: `${task.displayId || task.description?.trim() || "task"} — ${task.uid}`,
                title: [task.description?.trim(), task.supplierName, task.companyName]
                    .filter(Boolean).join(" · ") || undefined,
            })),
        },
    };

    let apiRootLabel;
    try {
        apiRootLabel = `Calls go to ${apiRoot()} from this page, so they appear in DevTools ▸ Network next to the app's own traffic.`;
    } catch (caught) {
        apiRootLabel = caught.message;
    }

    return (
        <div className={styles.root} ref={rootRef} style={height ? {height} : undefined}>
            <div className={styles.header}>
                <h1>API tool</h1>
                <div className={styles.session}><SessionBadge session={session}/></div>
                <span className={styles.spacer}/>
                <button onClick={loadSession} disabled={session == null}>Reload session</button>
            </div>

            <section className={styles.panel}>
                <div className={styles.row}>
                    <label>
                        Operation{" "}
                        <select value={endpointId} onChange={event => {
                            setEndpointId(event.target.value);
                            setParams(defaultParams(getEndpoint(event.target.value)));
                            setRun(null);
                            setError(null);
                        }}>
                            {endpoints.map(entry => (
                                <option key={entry.id} value={entry.id}>{entry.label}</option>
                            ))}
                        </select>
                    </label>

                    <div className={styles.params}>
                        {endpoint.params.map(param => (
                            <ParamField
                                key={param.name}
                                param={param}
                                value={params[param.name] ?? ""}
                                onChange={next => setParams({...params, [param.name]: next})}
                                sources={sources}
                            />
                        ))}
                    </div>
                </div>

                <div className={`${styles.row} ${styles.options}`}>
                    <label className={styles.check}>
                        <input
                            type="checkbox"
                            checked={options.ignoreCookieAuth}
                            onChange={event => setOptions({...options, ignoreCookieAuth: event.target.checked})}
                        />
                        <code>X-Ignore-Cookie-Auth: true</code>
                    </label>
                    <label className={styles.check}>
                        <input
                            type="checkbox"
                            checked={options.sendSessionHash}
                            onChange={event => setOptions({...options, sendSessionHash: event.target.checked})}
                        />
                        <code>X-Session-Hash</code>
                    </label>
                    <label className={styles.check}>
                        <input
                            type="checkbox"
                            checked={options.sendContextId}
                            onChange={event => setOptions({...options, sendContextId: event.target.checked})}
                        />
                        <code>Visma-ContextId</code>
                        <span className={styles.muted}>(approval-web omits it for my-tasks)</span>
                    </label>
                    <RunControls
                        endpoint={endpoint}
                        running={running}
                        armed={armed}
                        onArm={setArmed}
                        onRun={runSides}
                    />
                </div>

                <p className={styles.muted}>{apiRootLabel}</p>
            </section>

            {error && <section className={styles.error}>{error}</section>}

            {run &&
                <section className={run.which.length === 2 ? styles.sides : styles.singleSide}>
                    {run.which.map(key => <SidePanel key={key} apiKey={key} record={run[key]}/>)}
                </section>
            }

            {run?.comparison && <Comparison run={run}/>}
        </div>
    );
}

export default function ApiToolPage() {
    const enabled = launchDarkly.userClient?.variation("fe-api-tool", false);
    return enabled ? <ApiTool/> : <NotFound/>;
}
